/*
 * Copyright (c) 2026. World Wide Generation Ltd
 */

import '@g17eco/ui-modern/styles/tailwind.css';
import { Link } from 'react-router-dom';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@g17eco/ui-modern/components/ui/hover-card';
import { generateUrlWithQuery } from '@routes/util';
import { RouteInterfaceMin } from '../../../types/routes';
import { InitiativeLevel } from '@api/manage-users';

const MAX_NAMED_LEVELS = 5;

interface ManagedOnHoverCardProps {
  levels: InitiativeLevel[];
  /** The levels are below this one rather than a parent */
  managedOnSubsidiaries?: boolean;
  email?: string;
  baseRoute: RouteInterfaceMin;
}

// Counts unnamed and overflow levels; "another reporting level" when none are named
const getRestText = (namedCount: number, moreCount: number) => {
  if (namedCount) {
    return moreCount ? `and ${moreCount} more` : '';
  }
  return 'another reporting level';
};

export const ManagedOnHoverCard = (props: ManagedOnHoverCardProps) => {
  const { levels, managedOnSubsidiaries: isSubsidiaries, email, baseRoute } = props;
  const named = levels
    .filter((l): l is InitiativeLevel & { name: string } => Boolean(l.name))
    .slice(0, MAX_NAMED_LEVELS);
  const restText = getRestText(named.length, levels.length - named.length);
  const levelsText = [named.map((l) => `"${l.name}"`).join(', '), restText].filter(Boolean).join(' ');

  return (
    <HoverCard>
      <HoverCardTrigger asChild>
        {/* Screen readers and touch users don't get the card, so the label names the levels too */}
        <button
          type='button'
          className='tw:rounded-full tw:border-0 tw:bg-transparent tw:p-1 tw:outline-none tw:focus-visible:ring-[3px] tw:focus-visible:ring-ring/50'
          aria-label={`This user's access is managed on ${levelsText}, ${isSubsidiaries ? 'below' : 'above'} this level. You cannot manage this user from this level.`}
        >
          <i className='fa-light fa fa-circle-info' />
        </button>
      </HoverCardTrigger>
      <HoverCardContent align='start' className='tw:w-75 tw:text-sm tw:text-left'>
        <div className='tw:font-semibold'>
          <span className='tw:inline-block tw:w-5'>
            <i className={`fa-light fa ${isSubsidiaries ? 'fa-arrow-down' : 'fa-arrow-up'}`} aria-hidden='true' />
          </span>
          {isSubsidiaries ? 'Managed on subsidiaries' : 'Managed on a parent level'}
        </div>
        {/* Hanging indent: the rest lines up with the heading text, past the arrow */}
        <div className='tw:pl-5'>
          {named.length ? (
            <ul className='tw:my-1 tw:list-none tw:p-0'>
              {named.map((level) => (
                <li key={level._id} className='tw:truncate' title={level.name}>
                  {level.canManage ? (
                    <Link to={generateUrlWithQuery(baseRoute, { initiativeId: level._id, portfolioId: level._id }, { search: email })}>
                      {level.name}
                    </Link>
                  ) : (
                    level.name
                  )}
                </li>
              ))}
            </ul>
          ) : null}
          {restText ? <div className={named.length ? 'tw:text-muted-foreground' : undefined}>{restText}</div> : null}
          <div className='tw:mt-3 tw:text-xs tw:text-muted-foreground'>
            {named.some((l) => l.canManage) ? 'Open a level to manage this user.' : "You can't manage this user from here."}
          </div>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
};
